// src/lib/api.js
import { getMemToken, silentRefresh, clearSession } from "../auth";

const API_BASE = (process.env.REACT_APP_API_URL?.replace(/\/+$/, "")) || "";

// ── CSRF token (fetched once at boot, stored in memory) ──────────────────────
let _csrfToken = "";
let _csrfReady = false;

export async function initCsrf() {
  if (_csrfReady) return;          // already fetched — skip duplicate calls
  try {
    const data = await apiFetch("/api/auth/csrf-token", { _skipCsrf: true });
    _csrfToken = data?.csrfToken || "";
    _csrfReady = !!_csrfToken;     // only mark ready if we got a token
  } catch { /* non-fatal — next mutating request will just lack the header */ }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function needsBody(method) {
  return ["POST", "PUT", "PATCH", "DELETE"].includes(String(method || "GET").toUpperCase());
}

function isMutating(method) {
  return ["POST", "PUT", "PATCH", "DELETE"].includes(String(method || "GET").toUpperCase());
}

let _refreshing = false;
let _refreshQueue = [];

function enqueueRefresh(resolve, reject) {
  _refreshQueue.push({ resolve, reject });
}

function flushRefreshQueue(newToken, failed) {
  _refreshQueue.forEach(({ resolve, reject }) =>
    failed ? reject(new Error("Session expired")) : resolve(newToken)
  );
  _refreshQueue = [];
}

// ────────────────────────────────────────────────────────────────────────────
// Universal Fetch Wrapper
// ────────────────────────────────────────────────────────────────────────────
export async function apiFetch(path, opts = {}) {
  let cleanPath = path.startsWith("/") ? path : `/${path}`;
  if (API_BASE.endsWith("/api") && cleanPath.startsWith("/api/")) {
    cleanPath = cleanPath.substring(4);
  }
  const url = `${API_BASE}${cleanPath}`;

  const headers = new Headers(opts.headers || {});
  if (!headers.has("Content-Type") && !(opts.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Bearer token (in-memory only — never from localStorage)
  const token = getMemToken();
  if (token && (opts.auth || !headers.has("Authorization"))) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // CSRF header on all mutating requests
  if (isMutating(opts.method) && _csrfToken && !opts._skipCsrf) {
    headers.set("X-CSRF-Token", _csrfToken);
  }

  let body = opts.body;
  if (body && !(body instanceof FormData) && typeof body === "object") {
    body = JSON.stringify(body);
  }
  if (body && !needsBody(opts.method)) {
    headers.delete("Content-Type");
    body = undefined;
  }

  const controller = new AbortController();
  const tid        = setTimeout(() => controller.abort("timeout"), 30_000);
  const signal     = opts.signal || controller.signal;

  let res;
  try {
    res = await fetch(url, {
      ...opts,
      headers,
      body,
      signal,
      credentials: opts.credentials || "include",  // always send cookies
    });
  } catch (netErr) {
    clearTimeout(tid);
    if (netErr.name === "AbortError") throw netErr;
    const err = new Error("Network error. Please check your connection.");
    err.status = 0;
    throw err;
  }
  clearTimeout(tid);

  // ── 401: try silent refresh once ─────────────────────────────────────────
  if (res.status === 401 && token && !opts._isRetry) {
    if (_refreshing) {
      // Another request is already refreshing — queue this one
      return new Promise((resolve, reject) => {
        enqueueRefresh(resolve, reject);
      }).then(() => apiFetch(path, { ...opts, _isRetry: true }));
    }

    _refreshing = true;
    const refreshed = await silentRefresh();
    _refreshing = false;

    if (refreshed) {
      flushRefreshQueue(getMemToken(), false);
      return apiFetch(path, { ...opts, _isRetry: true });  // retry with new token
    } else {
      flushRefreshQueue(null, true);
      clearSession();
      window.location.href = "/login";
      const err = new Error("Session expired.");
      err.status = 401;
      throw err;
    }
  }

  // ── Blob response ─────────────────────────────────────────────────────────
  if (opts.responseType === "blob") {
    if (!res.ok) {
      const err = new Error(`${res.status} ${res.statusText}`);
      err.status = res.status;
      throw err;
    }
    return res.blob();
  }

  // ── JSON parsing ──────────────────────────────────────────────────────────
  const contentType = res.headers.get("content-type");
  let data;
  if (contentType?.includes("application/json")) {
    data = await res.json().catch(() => ({}));
  } else {
    data = { message: await res.text() };
  }

  if (!res.ok) {
    const msg = data?.message || `${res.status} ${res.statusText}`;
    const err = new Error(msg);
    err.status  = res.status;
    err.data    = data;
    // Only log non-401 (401 already handled above)
    if (res.status !== 401) console.error(`[apiFetch] ${res.status} ${msg}`, data);
    throw err;
  }

  return data;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export async function getProperties(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/api/properties?${qs}`, { method: "GET", auth: true });
}

export async function getCurrentUser() {
  return apiFetch("/api/auth/me", { method: "GET", auth: true });
}
